<template>
  <div>
    <v-list
      ref="header"
      class="work-sidebar__list work-siderbar__header" 
      dense
      flat
    >
      <v-list-item 
        class="work-siderbar-header-item"
      >
        <w-icon-button
          v-if="$vuetify.breakpoint.mdAndUp"
          :class="{
            'work-sidebar-icon': true, 
            'work-sidebar-icon--expanded': !mini
          }"
          :icon="mini ? '$wNavExpand' : '$wNavCollapse'"
          @click.stop="$emit('onClickSidebarIcon')"
        />
        <w-text-field
          ref="searchField"
          v-model="search"
          :placeholder="$t('NavigationView.search')"
          :class="{ 
            'work-sidebar-search-field': true, 
            'ml-3 pr-3': $vuetify.breakpoint.mdAndUp,
            'mx-2': $vuetify.breakpoint.smAndDown,
          }"
          prepend-inner-icon="$wNavSearch"
          rounded
        />
      </v-list-item>
    </v-list>

    <v-divider />

    <v-list
      ref="menu"
      class="work-sidebar__list work-sidebar__menu" 
      :style="{ '--sidebar-header-size': `${sidebarHeaderSize}px`}"
      dense
      flat
    >
      <v-list-item
        v-for="item in navigationTree"
        :key="item.id"
        :class="{
          'work-sidebar__content': true, 
          'work-sidebar-selected-item': getSelectedGroup == item.id,
          'reducedVisibility': !groupVisible(item),
        }"
      >
        <v-list-item-icon
          :class="{
            'work-sidebar-list-item-icon': true,
            'work-sidebar-list-item-icon--desktop': $vuetify.breakpoint.mdAndUp,
            'work-sidebar-list-item-icon--mobile': $vuetify.breakpoint.smAndDown
          }"
        >
          <w-icon
            class="work-sidebar-icon"
            @click="activeGroup = item.id;"
          >
            {{ item.sidebarIcon }}
          </w-icon>
        </v-list-item-icon>

        <v-list-group
          :class="{
            'work-sidebar-group': true,
            'work-sidebar-group--mobile': $vuetify.breakpoint.smAndDown
          }"
          :value="activeGroup == item.id"
          no-action
        >
          <template #activator>
            <v-list-item-content
              class="work-sidebar-list-item-content"
            >
              <v-list-item-title class="group-title">
                {{ item.group }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
          <v-list-item
            v-for="child in item.items"
            :key="child.label"
            class="work-sidebar-item-child"
            :to="child.routerLink"
            @click="setSidebarDrawerIsOpen(false);"
          >
            <v-list-item-content
              :class="{'reducedVisibility': !childVisible(item, child)}"
            >
              <v-list-item-title>
                {{ child.label }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import {ref, watch} from 'vue';
import moduleRegistry from '@/configuration/module-registry';
import navigationMixin from '@/mixins/navigation-mixin';

export default {
  name: 'WorkSidebarList',
  mixins: [navigationMixin],
  props: {
    shadow: {type: Boolean, default: false},
    mini: {type: Boolean, default: true}
  },
  emits: ['onClickSidebarIcon'],
  setup() {
    const search = ref('');
    const header = ref(null);
    let sidebarHeaderSize = ref(0); 
   
    watch(header, () => {
      sidebarHeaderSize.value = parseInt(window.getComputedStyle(header.value.$el).height);
    });

    return {
      header, 
      sidebarHeaderSize,
      search, 
      activeGroup: '',
    }
  },
  computed: {
    ...mapGetters('sidebarDrawer', ['getSelectedGroup']),
    navigationTree() {
      const visibleModules = this.filterOnlyVisibleModules(moduleRegistry.tree);
      const mappedVisibleModules = this.mapVisibleModules(visibleModules);
      return this.removeEmptyModuleCluster(mappedVisibleModules);
    },
  },
  methods: {
    ...mapActions('sidebarDrawer', ['setSidebarDrawerIsOpen']),
    mapVisibleModules(modules) {
      return modules.map((item) => {
        const onlyAllowedModulesForUser = this.filterOnlyModulesUserIsAllowedToSee(item.children);

        return {
          group: item.label,
          items: this.mapItems(onlyAllowedModulesForUser),
          sidebarIcon: item.sidebarIcon,
          id: item.id
        }
      });
    },
    groupVisible(groupItem) {
      return groupItem.group.toLowerCase().includes(this.search.toLowerCase()) ||
        groupItem.items.find(c => c.label.toLowerCase().includes(this.search.toLowerCase()));
    },
    childVisible(groupItem, child) {
      return child.label.toLowerCase().includes(this.search.toLowerCase()) ||
        groupItem.group.toLowerCase().includes(this.search.toLowerCase())
    }
  },
};
</script>

<style lang="scss" scoped>
.work-sidebar__list{
  .reducedVisibility {
    opacity: $disabled-opacity;
  }

  .v-list-item--link:hover {
    cursor: pointer;
  }

  .v-list-item {
    padding: 0px !important;
  }

  //header
  ::v-deep .work-siderbar-header-item > .work-sidebar-icon > svg {
    color: $sidebar-header-color !important;
  }

  .work-siderbar-header-item > {
    .work-sidebar-icon {
      padding-left: 2px;
    }
    .work-sidebar-icon--expanded {
      margin-left: 8px;
    }
  }

  .work-sidebar-search-field {
    background-color: var(--v-work_card-base);
    
    ::v-deep .v-input__prepend-inner {
      margin: auto;
      margin-left: -10px;
      
      .v-input__icon > .v-icon > .v-icon__component{
        width: 16px;
        height: 16px;
      }
    }

    ::v-deep .v-input__control > .v-input__slot{
      height: 32px !important;
      min-height: 32px !important;

      .v-input__prepend-inner > .v-input__icon > .v-icon > svg {
        color: $sidebar-icon-color !important; 
      }
    }
  }

  //menu list
  &.work-sidebar__menu{
    max-height: calc(100dvh - #{$header-size} - var(--sidebar-header-size) - var(--maintenance-height)) !important;
    overflow-y: auto
  }

  .work-sidebar-list-item-icon{
    padding-left: 2px;

    &.work-sidebar-list-item-icon--desktop.v-list-item__icon{
      margin: 12px 14px;
    }
    &.work-sidebar-list-item-icon--mobile.v-list-item__icon{
      margin: 10px 14px;
    }
  }

  ::v-deep .work-sidebar__content {
    &.work-sidebar-selected-item > .v-list-item__icon > .v-icon.work-sidebar-icon > svg{
      color: $sidebar-active-item-color !important;
    }

    .v-list-item__icon > .v-icon.work-sidebar-icon > svg{
      color: $sidebar-icon-color !important; 
    }
  
    .work-sidebar-group > .v-list-group__header {
      padding: 0px;

      .v-list-group__header__append-icon{ 
        margin-right: 14px;

        .v-icon > svg {
          color: $sidebar-icon-color !important;
        }
      }
    }

    .work-sidebar-group--mobile .v-list-group__header__append-icon{
      margin-right: 10px;
    }
  }

  .v-list-group{
    width: 100% ;

    .group-title {
      font-weight: bold;
    }
    
    ::v-deep .v-list-group__header {
      padding-right: 0px;
    }

    .work-sidebar-item-child.v-list-item--active .v-list-item__title{
      color: $sidebar-active-item-color;
    }
  }
}


</style>